/* =============================================================================
 * M.M.LaFleur — sticky Add to Bag (mobile PDP).
 *
 * A fixed bar at the foot of the viewport carrying one full-width Add to Bag
 * button, matching the live theme's sticky bar. It fades out while the real buy
 * row is on screen — two identical buttons visible at once is confusing and
 * makes the page look broken — and returns once that row has scrolled past.
 *
 * STEPS ASIDE FOR EVERY OVERLAY (Sept 10 2026)
 * -------------------------------------------
 * mm-mobile.css section 8 pins this bar at z-index 2147483647, the browser
 * ceiling, so it clears support-chat launchers. That same value put it on top
 * of everything else in the theme too: tapping Add to Bag slid the cart out
 * UNDERNEATH the bar, and the hamburger drawer opened with the bar still
 * parked across its foot.
 *
 * Lowering the z-index is not the fix — the chat widget problem it solves is
 * real. Instead the bar hides whenever any overlay is open. Every overlay in
 * the theme marks itself the same way, data-open="true" on its root:
 *
 *   [data-mm-cart]     cart drawer          mm-cart.js
 *   [data-mm-drawer]   hamburger nav        mm-editorial.js
 *   [data-mm-modal]    quick add            mm-editorial.js
 *   [data-mm-look]     Shop this look       mm-editorial.js
 *   [data-mm-sg]       size guide           mm-sizeguide.js
 *   .mm-zoom           image zoom           mm-zoom.js
 *
 * so one attribute observer covers them all, including the ones built lazily
 * on first open. Hiding reuses data-visible, so it gets the same slide-down
 * transition as the scroll behaviour and the same visibility:hidden that takes
 * the button out of the tab order. When the overlay closes, the bar returns
 * only if the real buy row is still off screen.
 *
 * FULL WIDTH, NO NAME OR PRICE (Sept 2026)
 * ---------------------------------------
 * The bar used to carry the product name and price beside a compact button.
 * Both are gone so the button spans the bar, as it does on live. The width is
 * set inline on the button below rather than in mm-mobile.css: section 8 there
 * sizes the button `flex: none` for the old two-column layout, and an inline
 * declaration outranks it without retransmitting that 22KB file through the
 * API. If mm-mobile.css is ever opened for another reason, move it there and
 * drop the .mm-sticky-atc-info / -name / -price rules, which are now inert.
 *
 * TWO BEHAVIOURS, DECIDED BY WHETHER A SIZE IS CHOSEN
 * ---------------------------------------------------
 *   size chosen      the button adds to bag
 *   nothing chosen   the button still reads "Add to Bag", but tapping it
 *                    returns the shopper to the buy row, marking the size chips
 *                    so it is obvious why the page moved
 *
 * The label is the same in both states — the action is named, and the size
 * grid supplies the missing input. That matches the in-page button, which
 * mm-size-gate.js holds at "Add to Bag" while it waits for a size.
 *
 * One exception: when EVERY size is sold out, the bar mirrors the real
 * button's "Sold out" and goes disabled. A bar inviting the shopper to add to
 * bag, only to scroll them to a grid of greyed-out chips, would be lying.
 *
 * WRITES ARE GUARDED — THIS FILE USED TO SPIN
 * -------------------------------------------
 * init() runs from a MutationObserver on the whole document, and syncButton()
 * used to assign textContent unconditionally. Assigning textContent replaces
 * the text node even when the string is identical, which is itself a childList
 * mutation — so every sync scheduled the next one, and the PDP ran a DOM write
 * on every animation frame for as long as it was open. setLabel() only writes
 * when the text actually differs, which breaks the loop. applyVisibility()
 * follows the same rule for data-visible.
 *
 * ADDING IS A PROXIED CLICK, NEVER A SECOND CART CALL
 * ---------------------------------------------------
 * The bar dispatches a click on the real [data-mm-submit] button. It does not
 * post to /cart/add.js itself.
 *
 * That distinction is the whole safety argument. The variant id is written onto
 * a hidden input by mm-editorial.js when a chip is tapped, and the handler that
 * posts it lives in the form. A cart call here would be a second copy of that
 * chain, free to drift from the first — and the first thing to drift would be
 * the variant id, which is the bug where a shopper adds the wrong size and
 * nobody notices until it ships. Clicking the real button runs the code that
 * already exists, so there is exactly one cart path and one variant id.
 *
 * The bar is built in JS and appended to <body> rather than rendered from
 * mm_product.liquid. That file is ~70KB, past the size worth rewriting through
 * the API, and a fixed element is better off outside the section anyway —
 * Platter's header wrapper takes a transform, and a transformed ancestor
 * becomes the containing block for position: fixed. The same trap
 * mm-nav-drawer.js documents.
 * ========================================================================== */

(function () {
  "use strict";

  if (window.__mmStickyAtcBound) return;
  window.__mmStickyAtcBound = true;

  var BAR_ID = "mm-sticky-atc";
  var PROMPT = "Add to Bag";

  /* See "STEPS ASIDE FOR EVERY OVERLAY" above. Add a selector here if a new
     overlay is built — as long as it sets data-open="true" on its root, the
     observer below already watches for it. */
  var OVERLAYS = [
    "[data-mm-cart]",
    "[data-mm-drawer]",
    "[data-mm-modal]",
    "[data-mm-look]",
    "[data-mm-sg]",
    ".mm-zoom"
  ].join(",");

  var bar = null;
  var realBtn = null;
  var labelObserver = null;
  var viewObserver = null;
  var pending = null;

  /* Starts true so a freshly built bar stays hidden until the
     IntersectionObserver has reported where the buy row actually is. */
  var buyRowOnScreen = true;

  function teardown() {
    if (labelObserver) { labelObserver.disconnect(); labelObserver = null; }
    if (viewObserver) { viewObserver.disconnect(); viewObserver = null; }
    if (bar && bar.parentNode) bar.parentNode.removeChild(bar);
    bar = null;
    realBtn = null;
    buyRowOnScreen = true;
    document.body.classList.remove("mm-sticky-atc-on");
  }

  /* See "WRITES ARE GUARDED" above. */
  function setLabel(btn, text) {
    if (btn.textContent !== text) btn.textContent = text;
  }
  function setDisabled(btn, value) {
    if (btn.disabled !== value) btn.disabled = value;
  }

  function overlayOpen() {
    var nodes = document.querySelectorAll(OVERLAYS);
    for (var i = 0; i < nodes.length; i++) {
      if (nodes[i].getAttribute("data-open") === "true") return true;
    }
    return false;
  }

  /* The single place data-visible is decided: shown only when the real buy row
     is off screen AND nothing is laid over the page. */
  function applyVisibility() {
    if (!bar) return;
    var next = String(!buyRowOnScreen && !overlayOpen());
    if (bar.getAttribute("data-visible") !== next) bar.setAttribute("data-visible", next);
  }

  function hasSizeGroup() {
    return !!document.querySelector("[data-mm-size-group]");
  }

  /* Published by mm-size-gate.js. Falling back to the chip query rather than
     assuming true keeps the bar honest if that script fails to load — the
     failure mode is "returns you to the buy row", not "adds the wrong thing". */
  function sizeChosen() {
    var form = document.querySelector("[data-mm-product-form]");
    var flag = form && form.getAttribute("data-mm-size-chosen");
    if (flag === "true") return true;
    if (flag === "false") return false;

    if (!hasSizeGroup()) return true;
    var group = document.querySelector("[data-mm-size-group]");
    return !!(group && group.querySelector('button[aria-pressed="true"]'));
  }

  /* Every size chip disabled means nothing on this product can be bought.
     mm_product.liquid disables a chip when its variant is unavailable, so this
     is true exactly when the whole product is sold out. */
  function allSizesSoldOut() {
    var group = document.querySelector("[data-mm-size-group]");
    if (!group) return false;
    var chips = group.querySelectorAll("button[data-mm-size]");
    if (!chips.length) return false;
    for (var i = 0; i < chips.length; i++) {
      if (!chips[i].disabled) return false;
    }
    return true;
  }

  /* With a size chosen the bar mirrors the real button, so it goes on reporting
     "Add to bag", "Sold out", and the transient "Added to bag" that
     mm-editorial.js flashes after a successful add. Without one it carries its
     own label instead. */
  function syncButton() {
    if (!bar || !realBtn) return;
    var btn = bar.querySelector("[data-mm-sticky-submit]");
    if (!btn) return;

    if (allSizesSoldOut()) {
      setLabel(btn, realBtn.textContent.trim() || "Sold out");
      setDisabled(btn, true);
      return;
    }

    if (!sizeChosen()) {
      setLabel(btn, PROMPT);
      setDisabled(btn, false);
      if (btn.hasAttribute("aria-describedby")) btn.removeAttribute("aria-describedby");
      return;
    }

    setLabel(btn, realBtn.textContent.trim());

    /* Sold out is genuinely unavailable, so the bar goes disabled with it. */
    setDisabled(btn, !!realBtn.disabled);
  }

  function returnToBuyRow() {
    var form = document.querySelector("[data-mm-product-form]");
    var buy = form ? form.querySelector(".mm-pdp-buy") : null;
    var dest = buy || form;
    if (dest) dest.scrollIntoView({ behavior: "smooth", block: "center" });

    /* A brief marker on the size chips, so it is obvious WHY the page moved
       rather than leaving the shopper to work out what changed. */
    var group = document.querySelector("[data-mm-size-group]");
    if (group) {
      group.setAttribute("data-mm-nudge", "true");
      window.setTimeout(function () { group.removeAttribute("data-mm-nudge"); }, 1200);
    }
  }

  function build(form) {
    bar = document.createElement("div");
    bar.id = BAR_ID;
    bar.className = "mm-sticky-atc mm-scope";
    bar.setAttribute("data-visible", "false");

    var btn = document.createElement("button");
    btn.type = "button";
    btn.className = "mm-sticky-atc-btn";
    btn.setAttribute("data-mm-sticky-submit", "");

    /* Full width. See the note at the top of this file for why this is inline. */
    btn.style.flex = "1 1 auto";
    btn.style.width = "100%";

    btn.addEventListener("click", function () {
      if (!sizeChosen()) {
        returnToBuyRow();
        return;
      }

      /* The one cart path. See the note at the top of this file. */
      if (realBtn && !realBtn.disabled) realBtn.click();
    });

    bar.appendChild(btn);
    document.body.appendChild(bar);
    document.body.classList.add("mm-sticky-atc-on");

    syncButton();

    labelObserver = new MutationObserver(syncButton);
    labelObserver.observe(realBtn, {
      childList: true,
      characterData: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["disabled"]
    });

    /* The gate fires when a chip is tapped. Without this the bar could keep
       its waiting state after the shopper has chosen a size from the grid,
       because the real button's own label does not change at that moment — it
       is revealed rather than relabelled. */
    document.addEventListener("mm:size-gate", syncButton);

    /* Visibility. The bar is shown whenever the real buy row is NOT on
       screen, which covers both directions without tracking scroll position:
       above it on the way down, and below it on the way back up. */
    var target = form.querySelector(".mm-pdp-buy") || form;
    viewObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        buyRowOnScreen = entry.isIntersecting;
      });
      applyVisibility();
    }, { rootMargin: "0px 0px -40px 0px" });
    viewObserver.observe(target);
  }

  function init() {
    pending = null;

    var form = document.querySelector("[data-mm-product-form]");
    var submit = form ? form.querySelector("[data-mm-submit]") : null;

    /* Not a PDP, or Barba has swapped one out. Either way the old bar must go
       — it lives on <body>, outside the swap container, so nothing else would
       ever remove it and it would follow the shopper onto the next page. */
    if (!form || !submit) {
      if (bar) teardown();
      return;
    }

    /* Same product, already wired. */
    if (bar && realBtn === submit) {
      syncButton();
      return;
    }

    if (bar) teardown();
    realBtn = submit;
    build(form);
  }

  function schedule() {
    if (pending) return;
    pending = window.requestAnimationFrame(init);
  }

  function watchOverlays() {
    /* Bound once for the session, not per bar: the overlays outlive any one
       PDP, and the size guide and zoom are only created on first open. Filtered
       to data-open so this sees nothing but overlay (and accordion) toggles,
       and applyVisibility() is a no-op when no bar exists. */
    if (!window.MutationObserver) return;
    new MutationObserver(applyVisibility).observe(document.body, {
      attributes: true,
      attributeFilter: ["data-open"],
      subtree: true
    });
  }

  function start() {
    init();
    watchOverlays();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }

  document.addEventListener("shopify:section:load", schedule);
  window.addEventListener("popstate", schedule);

  /* Barba swaps <main> without firing an event this can listen for, the same
     reason mm-card-swatches.js watches the DOM. */
  if (window.MutationObserver) {
    new MutationObserver(schedule).observe(document.documentElement, {
      childList: true,
      subtree: true
    });
  }
}());
